<script setup lang="ts">
    import type { TrainingCourseDetail } from '~/types/academy/baseTraining';
    import GradientCircle from '~/assets/images/gradient-circle-white.svg';
    import AcademyHeroBackgroundAnimated from '~/storyblok/Academy/AcademyBackgroundAnimated.vue';

    defineProps<{
        isLoading?: boolean;
        training?: Pick<TrainingCourseDetail, 'title' | 'shortDescription' | 'image' | 'badges'>;
    }>();
</script>

<template>
    <section
        class="hero-container academy-training-hero"
        :aria-busy="isLoading || undefined"
        :aria-label="isLoading ? $t('academy.training.loadingAriaLabel') : undefined"
    >
        <div class="hero-wrapper">
            <div class="content">
                <div class="headline-container">
                    <AcademyTrainingHeroLoadingSkeleton v-if="isLoading" />

                    <template v-else-if="training">
                        <TeaserBadges
                            v-if="training.badges.length"
                            :badges="training.badges"
                            class="hero-teaser-badges"
                        />

                        <h1>{{ training.title }}</h1>

                        <p
                            v-if="training.shortDescription"
                            class="short-description"
                        >
                            {{ training.shortDescription }}
                        </p>
                    </template>
                </div>
            </div>

            <div class="media-content">
                <img
                    :src="GradientCircle"
                    class="gradient-circle"
                    alt=""
                />
            </div>
        </div>

        <HeroScrollDown />
        <AcademyHeroBackgroundAnimated variant="training" />
    </section>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/academy/academy-hero-teaser.scss';

    .academy-training-hero {
        --training-hero-media-sink: 7%;

        height: auto;
        max-height: none;
        min-height: 22rem;
        padding: 0;

        .hero-wrapper {
            min-height: inherit;
        }

        .media-content {
            align-items: flex-end;
            align-self: flex-start;
            display: flex;
            height: auto;
            margin-bottom: 0;
            min-height: 0;
            position: relative;
            width: min(80vw, 18rem);
        }

        :deep(.hero-media__wrapper) {
            aspect-ratio: 1 / 1;
            left: 0;
            max-width: none;
            position: relative;
            right: auto;
            transform: translateY(var(--training-hero-media-sink));
            width: 100%;
        }

        .hero-wrapper .gradient-circle {
            bottom: 0;
            left: 0;
            right: auto;
            transform: translate(-6%, calc(-6% + var(--training-hero-media-sink))) scale(1.04);
            transform-origin: center;
            width: 100%;
        }

        .hero-media {
            height: auto;
            position: relative;
            width: 100%;
            z-index: 1;
        }

        @media (min-width: $br-m) {
            min-height: 24rem;
            padding-top: var(--spacing--2);
            padding-bottom: var(--spacing--2);

            .media-content {
                align-self: auto;
                display: block;
                height: 100%;
                min-height: 100%;
                width: auto;
            }

            :deep(.hero-media__wrapper) {
                bottom: 0;
                left: auto;
                max-width: min(100%, 32rem);
                position: absolute;
                right: 0;
                width: 100%;
            }

            .hero-wrapper .gradient-circle {
                left: auto;
                max-width: none;
                right: 0;
                transform: translate(-4%, calc(-4% + var(--training-hero-media-sink))) scale(1.04);
                width: min(100%, 32rem);
            }

            .hero-media {
                height: 100%;
                width: 100%;
                z-index: auto;
            }
        }

        @media (min-width: $br-l) {
            min-height: 28rem;
        }
    }

    .hero-container {
        .hero-wrapper {
            .headline-container {
                position: relative;
                display: block;

                @media (min-width: $br-m) {
                    max-width: calc(100vw - var(--cta-card-width, 50%) - var(--spacing) * 4);
                }

                h1 {
                    color: var(--black);
                    font-family: var(--font-hero-heading);
                    font-size: 2.25rem !important;
                    font-weight: bold !important;
                    line-height: 1.1;
                    margin: var(--spacing) 0;
                    overflow-wrap: break-word;

                    @media (min-width: $br-l) {
                        font-size: 2.625rem !important;
                    }
                }

                p {
                    font-size: 1.25rem;
                    font-weight: bold;

                    @media (min-width: $br-l) {
                        font-size: 1.375rem;
                    }
                }
            }
        }
    }

    .short-description {
        color: var(--black);
        font-size: 1.125rem;
        line-height: 1.5;
        margin: var(--spacing--1) 0 0;
    }

    .hero-teaser-badges {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: var(--spacing--1);
    }
</style>
